import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme, ThemeMode } from './index';

// Create a context for theme mode
interface ThemeContextType {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use the theme context
export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  // Try to get saved preference from localStorage, default to 'system'
  const getInitialMode = (): ThemeMode => {
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('themeMode') as ThemeMode;
      return savedMode || 'system';
    }
    return 'system';
  };

  const [mode, setMode] = useState<ThemeMode>(getInitialMode);
  
  // Determine the actual theme mode (light/dark) based on system preference if needed
  const [actualMode, setActualMode] = useState<'light' | 'dark'>('light');
  
  // Handle theme changes
  useEffect(() => {
    // Save preference to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeMode', mode);
    }
    
    // Determine actual mode to apply
    if (mode === 'system') {
      const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setActualMode(prefersDarkMode ? 'dark' : 'light');
      
      // Add listener for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setActualMode(e.matches ? 'dark' : 'light');
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      setActualMode(mode === 'dark' ? 'dark' : 'light');
    }
  }, [mode]);
  
  // Function to toggle between light/dark modes
  const toggleColorMode = () => {
    setMode((prevMode) => {
      if (prevMode === 'light') return 'dark';
      if (prevMode === 'dark') return 'system';
      return 'light';
    });
  };
  
  // Mirror the resolved mode onto the DOM so the CSS design tokens switch in lockstep with MUI.
  // The token file (colors_and_type.css) keys its dark values off [data-theme="dark"]; without this
  // the tokens stay frozen on their light values while MUI components go dark, leaving native
  // heading tags (h1..h6) painted with the light --color-text-heading on a dark surface.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', actualMode);
    }
  }, [actualMode]);

  // Generate the theme based on the actual mode
  const theme = useMemo(() => createAppTheme(actualMode), [actualMode]);
  
  // Create context value
  const contextValue = useMemo(
    () => ({
      mode,
      resolvedMode: actualMode,
      setMode,
      toggleColorMode,
    }),
    [mode, actualMode]
  );
  
  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};